import { createRng } from './rng';
import type { SampleInvoice } from './invoices';

/**
 * VAT Center aggregator — used by:
 *   - Customer Portal /vat (period-by-period output / input VAT)
 *   - Customer Portal Compliance Centre TDD readiness ribbon
 *   - Admin Console /tickstar "TDD analytics" tab
 *
 * UAE B2B VAT periods: monthly for large taxpayers (turnover > 150M AED) and
 * quarterly for the rest. We default to monthly for richer demos.
 */

export type VatPeriodKind = 'month' | 'quarter';

export interface VatPeriodSummary {
  /** Period code, e.g. "2026-04" or "2026-Q1". */
  period: string;
  kind: VatPeriodKind;
  /** Output VAT (collected from sales). */
  outputVatAed: number;
  /** Input VAT (claimable on purchases). */
  inputVatAed: number;
  /** Net payable / refundable to FTA (output - input). */
  netPayableAed: number;
  /** Standard-rated taxable supplies AED. */
  standardSuppliesAed: number;
  /** Zero-rated supplies AED. */
  zeroRatedSuppliesAed: number;
  /** Exempt supplies AED. */
  exemptSuppliesAed: number;
  /** TDD submission status for this period. */
  tddStatus: 'submitted' | 'pending' | 'overdue';
  /** TDD deadline (ISO date). */
  tddDeadline: string;
  /** Number of invoices contributing to this period. */
  invoiceCount: number;
  /** Late filings flagged. */
  lateInvoices: number;
}

export interface VatHealthBreakdown {
  /** Vendors who haven't sent EmaraTax-compliant invoices. */
  nonCompliantSuppliers: { supplier: string; lastInvoice: string; reason: string }[];
  /** Tax-code distribution across line items, %. */
  taxCodeShare: { code: 'STANDARD' | 'ZERO' | 'EXEMPT' | 'OUT_OF_SCOPE'; share: number; aed: number }[];
}

export interface VatOverview {
  /** 12 most recent monthly periods, oldest first. */
  monthly: VatPeriodSummary[];
  /** Last 4 quarters. */
  quarterly: VatPeriodSummary[];
  /** Total YTD output VAT. */
  ytdOutputVatAed: number;
  /** Total YTD input VAT. */
  ytdInputVatAed: number;
  /** Total YTD net payable to FTA. */
  ytdNetPayableAed: number;
  /** Currently outstanding TDD obligations (count). */
  pendingTddCount: number;
  /** Refund expected from FTA (sum of negative net periods). */
  refundExpectedAed: number;
  health: VatHealthBreakdown;
}

function monthCode(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function quarterCode(d: Date): string {
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `${d.getUTCFullYear()}-Q${q}`;
}

function pickTddStatus(period: string, today: Date, rng: ReturnType<typeof createRng>): VatPeriodSummary['tddStatus'] {
  const cur = monthCode(today);
  if (period >= cur) return 'pending';
  return rng.chance(0.95) ? 'submitted' : 'overdue';
}

export function generateVatOverview(_invoices: SampleInvoice[], seed = 0x42_a3_91): VatOverview {
  const rng = createRng(seed);
  const now = new Date();
  const monthly: VatPeriodSummary[] = [];
  const months = 12;
  for (let i = months - 1; i >= 0; i--) {
    const dt = new Date(now);
    dt.setUTCMonth(dt.getUTCMonth() - i);
    const period = monthCode(dt);
    const baseStandard = rng.int(1_400_000, 4_500_000);
    const baseZero = rng.int(0, 800_000);
    const baseExempt = rng.int(0, 250_000);
    const outputVat = Math.round(baseStandard * 0.05);
    const inputVat = Math.round(outputVat * (0.45 + rng.next() * 0.55));
    const tddStatus = pickTddStatus(period, now, rng);
    const deadline = new Date(dt);
    deadline.setUTCMonth(deadline.getUTCMonth() + 1);
    deadline.setUTCDate(28);
    monthly.push({
      period,
      kind: 'month',
      outputVatAed: outputVat,
      inputVatAed: inputVat,
      netPayableAed: outputVat - inputVat,
      standardSuppliesAed: baseStandard,
      zeroRatedSuppliesAed: baseZero,
      exemptSuppliesAed: baseExempt,
      tddStatus,
      tddDeadline: deadline.toISOString().slice(0, 10),
      invoiceCount: rng.int(60, 220),
      lateInvoices: rng.int(0, 4),
    });
  }

  const quarterly: VatPeriodSummary[] = [];
  for (let q = 3; q >= 0; q--) {
    const dt = new Date(now);
    dt.setUTCMonth(dt.getUTCMonth() - q * 3);
    const period = quarterCode(dt);
    const baseStandard = rng.int(4_000_000, 12_000_000);
    const baseZero = rng.int(200_000, 1_800_000);
    const baseExempt = rng.int(0, 600_000);
    const outputVat = Math.round(baseStandard * 0.05);
    const inputVat = Math.round(outputVat * (0.5 + rng.next() * 0.45));
    const tddStatus = pickTddStatus(period, now, rng);
    const deadline = new Date(dt);
    deadline.setUTCMonth(deadline.getUTCMonth() + 1);
    deadline.setUTCDate(28);
    quarterly.push({
      period,
      kind: 'quarter',
      outputVatAed: outputVat,
      inputVatAed: inputVat,
      netPayableAed: outputVat - inputVat,
      standardSuppliesAed: baseStandard,
      zeroRatedSuppliesAed: baseZero,
      exemptSuppliesAed: baseExempt,
      tddStatus,
      tddDeadline: deadline.toISOString().slice(0, 10),
      invoiceCount: rng.int(180, 620),
      lateInvoices: rng.int(0, 8),
    });
  }

  const ytdOutputVatAed = monthly.reduce((s, m) => s + m.outputVatAed, 0);
  const ytdInputVatAed = monthly.reduce((s, m) => s + m.inputVatAed, 0);
  const ytdNetPayableAed = ytdOutputVatAed - ytdInputVatAed;
  const pendingTddCount = monthly.filter((m) => m.tddStatus === 'pending').length
    + monthly.filter((m) => m.tddStatus === 'overdue').length;
  const refundExpectedAed = monthly
    .filter((m) => m.netPayableAed < 0)
    .reduce((s, m) => s + Math.abs(m.netPayableAed), 0);

  const taxCodeShare = (() => {
    const shares = [
      { code: 'STANDARD' as const, base: 0.78 },
      { code: 'ZERO' as const,     base: 0.12 },
      { code: 'EXEMPT' as const,   base: 0.07 },
      { code: 'OUT_OF_SCOPE' as const, base: 0.03 },
    ];
    return shares.map((s) => ({
      code: s.code,
      share: s.base,
      aed: Math.round(ytdOutputVatAed / 0.05 * s.base),
    }));
  })();

  const nonCompliantSuppliers = [
    { supplier: 'Damac Maintenance Services',  lastInvoice: '2026-03-22', reason: 'Sent PDF only, no PINT-AE UBL' },
    { supplier: 'Apparel Group LLC',            lastInvoice: '2026-03-18', reason: 'Buyer TRN missing on header' },
    { supplier: 'Spinneys Dubai',                lastInvoice: '2026-03-12', reason: 'AED currency code missing' },
    { supplier: 'Lulu Hypermarket FZE',          lastInvoice: '2026-03-09', reason: 'Wrong TaxCategory code (S vs E)' },
  ].slice(0, rng.int(2, 4));

  return {
    monthly,
    quarterly,
    ytdOutputVatAed,
    ytdInputVatAed,
    ytdNetPayableAed,
    pendingTddCount,
    refundExpectedAed,
    health: { nonCompliantSuppliers, taxCodeShare },
  };
}
